/**
 * Centralized UI state management with Zustand.
 *
 * Manages global UI state including modals, sidebar, and edit modes.
 * Uses immer middleware for immutable state updates.
 *
 * @example
 * // Open a modal
 * const openModal = useUIStore((s) => s.openModal)
 * openModal("new-company", { defaultValues: { name: "Acme Inc" } })
 *
 * @example
 * // Subscribe to specific state (performance optimization)
 * const { type, props } = useModal()
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';

type ConfirmDeleteModalProps = {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning';
  onConfirm?: () => void | Promise<void>;
  isLoading?: boolean;
};

type ModalPropsByType = {
  'new-company': Record<string, unknown>;
  'new-site': Record<string, unknown>;
  'new-unit': Record<string, unknown>;
  'new-personnel': Record<string, unknown>;
  'new-action': Record<string, unknown>;
  'new-report': Record<string, unknown>;
  'entity-picker': Record<string, unknown>;
  'confirm-delete': ConfirmDeleteModalProps;
};

/**
 * Modal types - centralized registry of all application modals
 */
export const MODAL_TYPES = [
  'new-company',
  'new-site',
  'new-unit',
  'new-personnel',
  'new-action',
  'new-report',
  'entity-picker',
  'confirm-delete',
] as const;

export type ModalType = (typeof MODAL_TYPES)[number];

/**
 * Modal state shape
 */
type ModalState =
  | { type: null; props: Record<string, never> }
  | {
      [K in ModalType]: {
        type: K;
        props: ModalPropsByType[K];
      };
    }[ModalType];

/**
 * Complete UI store state
 */
interface UIState {
  // Modal state
  modal: ModalState;
  openModal: <T extends ModalType>(type: T, props?: ModalPropsByType[T]) => void;
  closeModal: () => void;
  // Local dialog states
  localDialogs: Record<string, boolean>;
  setLocalDialog: (key: string, open: boolean) => void;

  // Sidebar state
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Report edit mode
  editingReportId: string | null;
  setEditingReport: (reportId: string | null) => void;

  // Global loading state (optional, for app-wide loading indicators)
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
}

/**
 * UI state store
 */
export const useUIStore = createWithEqualityFn<UIState>()(
  immer((set) => ({
    // Modal state
    modal: {
      type: null,
      props: {},
    },
    openModal: (type, props) =>
      set((state) => {
        state.modal = {
          type,
          props: (props ?? {}) as ModalPropsByType[typeof type],
        } as ModalState;
      }),
    closeModal: () =>
      set((state) => {
        if (state.modal.type === null) return;
        state.modal = { type: null, props: {} };
      }),

    // Local dialog states
    localDialogs: {},
    setLocalDialog: (key, open) =>
      set((state) => {
        if (open) {
          state.localDialogs[key] = true;
        } else {
          delete state.localDialogs[key];
        }
      }),

    // Sidebar state
    sidebarCollapsed: false,
    toggleSidebar: () =>
      set((state) => {
        state.sidebarCollapsed = !state.sidebarCollapsed;
      }),
    setSidebarCollapsed: (collapsed) =>
      set((state) => {
        state.sidebarCollapsed = collapsed;
      }),

    // Report editing state
    editingReportId: null,
    setEditingReport: (reportId) =>
      set((state) => {
        state.editingReportId = reportId;
      }),

    // Global loading state
    globalLoading: false,
    setGlobalLoading: (loading) =>
      set((state) => {
        state.globalLoading = loading;
      }),
  }))
);

/**
 * Selector hooks for performance optimization
 * Only subscribe to the specific slice of state you need
 */

/**
 * Subscribe to modal state only
 */
export const useModal = () =>
  useUIStore(
    useShallow((state) => ({
      modal: state.modal,
      open: state.openModal,
      close: state.closeModal,
    }))
  );
